import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, CheckCircle, Building2, Smartphone, Banknote, Copy, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Which payment options exist, and the account details behind them, come from
// the register admin maintains in Settings — nothing about them is fixed here.
const MODE_ICONS = { bank_transfer: Building2, mobile_money: Smartphone, cheque: Banknote, cash: Banknote, other: Banknote };

const accountRows = (account) => [
  ['Bank / provider', account.provider],
  ['Account name', account.account_name],
  [account.mode === 'mobile_money' ? 'Lipa Namba' : 'Account number', account.account_number],
  ['Branch', account.branch],
  ['SWIFT', account.swift_code],
].filter(([, value]) => value);

export default function CustomerCheckout() {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [formData, setFormData] = useState({
    delivery_address: '',
    payment_reference: '',
    notes: '',
  });

  useEffect(() => {
    const userData = localStorage.getItem('customer_user');
    const token = localStorage.getItem('customer_token');
    if (!token || !userData) {
      navigate('/customer/login');
      return;
    }
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    setFormData((p) => ({ ...p, delivery_address: parsedUser.address || '' }));
    fetchCart();
    fetchPaymentAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCart = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      const res = await axios.get(`${API}/customer/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.data.items || res.data.items.length === 0) {
        toast.error('Your cart is empty');
        navigate('/customer/products');
        return;
      }
      setCart(res.data);
    } catch (_) {
      toast.error('Failed to load cart');
      navigate('/customer/cart');
    }
  };

  const fetchPaymentAccounts = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      const res = await axios.get(`${API}/customer/payment-accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data || [];
      setAccounts(list);
      if (list.length) setAccountId(list[0].id);
    } catch (_) {
      toast.error('Could not load payment options');
    }
  };

  const total = cart.items?.reduce((s, i) => s + i.total_price, 0) || 0;
  const account = accounts.find((a) => a.id === accountId);
  // Cash is settled on delivery; everything else is paid first and needs a reference
  const needsReference = !!account && account.mode !== 'cash';

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (_) {
      toast.error('Could not copy to clipboard');
    }
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (!formData.delivery_address.trim()) {
      toast.error('Please enter delivery address');
      return;
    }
    if (!account) {
      toast.error('Please choose how you will pay');
      return;
    }
    if (needsReference) {
      if (!formData.payment_reference.trim()) {
        toast.error('Please enter your payment reference number to proceed');
        return;
      }
      if (formData.payment_reference.trim().length < 4) {
        toast.error('Payment reference looks too short — please check');
        return;
      }
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('customer_token');
      const orderData = {
        items: cart.items,
        subtotal: total,
        total_amount: total,
        payment_account_id: account.id,
        payment_reference: needsReference ? formData.payment_reference.trim() : '',
        delivery_address: formData.delivery_address,
        notes: formData.notes,
      };
      await axios.post(`${API}/customer/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Order placed successfully! Awaiting approval.');
      navigate('/customer/orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <img src="/himan-logo.png" alt="Himan Logo" className="h-12 w-12" />
            <div>
              <h1 className="text-xl font-bold">Checkout</h1>
              <p className="text-sm text-gray-600">Complete your order</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <form onSubmit={handlePlaceOrder} className="space-y-6">
              {/* Delivery */}
              <Card>
                <CardHeader>
                  <CardTitle>Delivery Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="delivery_address">Delivery Address *</Label>
                    <Input
                      id="delivery_address"
                      data-testid="delivery-address-input"
                      value={formData.delivery_address}
                      onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                      placeholder="Enter full delivery address"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes (Optional)</Label>
                    <Input
                      id="notes"
                      data-testid="notes-input"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Any special instructions?"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Payment method */}
              <Card>
                <CardHeader>
                  <CardTitle>Payment Method *</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {accounts.length === 0 ? (
                    <p className="text-sm text-gray-600">
                      No payment option is available right now — please contact us to complete your order.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {accounts.map((opt) => {
                        const Icon = MODE_ICONS[opt.mode] || Banknote;
                        const active = accountId === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setAccountId(opt.id)}
                            data-testid={`payment-option-${opt.id}`}
                            className={`p-4 border-2 rounded-lg text-left transition-all ${
                              active
                                ? 'border-[#8FC043] bg-[#8FC043]/5 shadow-sm'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Icon size={18} className={active ? 'text-[#8FC043]' : 'text-gray-500'} />
                              {active && (
                                <Badge className="bg-[#8FC043] text-white text-[10px]">Selected</Badge>
                              )}
                            </div>
                            <div className="text-sm font-semibold text-gray-900">{opt.label}</div>
                            <div className="text-xs text-gray-500">{opt.mode_label}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Account details for the selected option */}
                  {account && (accountRows(account).length > 0 || account.instructions) && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Info size={16} className="text-[#EA5829]" />
                        <h4 className="font-semibold text-sm">
                          {needsReference ? 'Send Payment To:' : 'How this works:'}
                        </h4>
                      </div>
                      <div className="space-y-2">
                        {accountRows(account).map(([label, value]) => (
                          <div key={label} className="flex items-center justify-between gap-3 bg-white rounded px-3 py-2 border">
                            <div className="min-w-0">
                              <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
                              <div className="text-sm font-mono font-semibold text-gray-900 truncate">{value}</div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(value, label)}
                              data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}
                            >
                              <Copy size={12} />
                            </Button>
                          </div>
                        ))}
                      </div>
                      {account.instructions && (
                        <p className="text-xs text-slate-600 mt-3">{account.instructions}</p>
                      )}
                      {needsReference && (
                        <p className="text-xs text-slate-600 mt-3">
                          Send exactly <b>Tsh {total.toLocaleString()}</b> to the account above, then enter the
                          reference number below to complete your order.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Payment reference — only where the customer pays before delivery */}
                  {needsReference && (
                    <div className="space-y-2 pt-2">
                      <Label htmlFor="payment_reference">
                        Payment Reference Number <span className="text-rose-600">*</span>
                      </Label>
                      <Input
                        id="payment_reference"
                        data-testid="payment-reference-input"
                        value={formData.payment_reference}
                        onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
                        placeholder="The confirmation code from your payment"
                        required
                      />
                      <p className="text-xs text-slate-600">
                        This helps us match your payment to this order.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Review notice */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#8FC043] mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h3 className="font-semibold">Order Review Process</h3>
                      <p className="text-sm text-gray-700 mt-1">
                        Once we verify the payment reference against our records, your order will be approved
                        and you&apos;ll receive an SMS confirmation. If we can&apos;t find a matching payment, our team
                        will contact you.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button
                type="submit"
                className="w-full bg-[#8FC043] hover:bg-[#7aa936]"
                size="lg"
                disabled={loading}
                data-testid="place-order-button"
              >
                {loading ? <Loader2 className="animate-spin" /> : `Place Order — Tsh ${total.toLocaleString()}`}
              </Button>
            </form>
          </div>

          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Items ({cart.items?.length || 0})</p>
                  {cart.items?.map((item) => (
                    <div key={item.product_id} className="flex justify-between text-sm py-2 border-b">
                      <span>{item.product_name} × {item.quantity}</span>
                      <span className="font-medium">Tsh {item.total_price.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-[#EA5829]" data-testid="checkout-total">
                      Tsh {total.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="text-xs text-gray-600">
                  <p>
                    Customer Type: <span className="font-semibold uppercase">{user?.customer_type}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
